import { getFullName } from "@/entities/people/helpers";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { PersonData, isReservedPersonId } from "@/entities/people/types";
import { UserData } from "@/entities/users/types";
import { EntityData, Filters } from "@/services/api/types";
import { useEntitySearch } from "@/services/hooks/entities/useEntitySearch";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

export const getSingleSelectOption = (
  entity: EntityData,
  entityType: EntityType,
  isHidden: boolean = false
) => {
  let displayName = "";
  let subtitle = "";

  if (entityType === EntityType.USER) {
    const userData = entity as UserData;
    displayName = userData.displayName || "";
    subtitle = userData.jobTitle || "";
  } else if (entityType === EntityType.PERSON) {
    displayName = getFullName(entity as PersonData);
    subtitle = !isEmpty(entity?.jobTitle) ? `${entity?.jobTitle}` : "";
  } else if (entityType === EntityType.PARTNER_CONTACT) {
    displayName = getPartnerContactFullName(entity as PartnerContactData);
    subtitle = !isEmpty(entity?.jobTitle) ? `${entity?.jobTitle}` : "";
  } else {
    displayName = entity?.name || "";
  }

  return {
    id: entity.id,
    displayName,
    subtitle,
    isHidden: isHidden,
    userData: entityType === EntityType.USER ? entity : undefined
  };
};

interface UseEntitySelectProps {
  entityType: EntityType;
  selectedId?: string | null;
  filters?: Filters;
  excludeIds?: string[];
  autoSelectSingleOption?: boolean;
  onAutoSelect?: (id: string | number | null) => void;
  alwaysEnabled?: boolean;
}

export const useEntitySelect = ({
  entityType,
  selectedId,
  filters,
  excludeIds,
  autoSelectSingleOption = false,
  onAutoSelect,
  alwaysEnabled = false
}: UseEntitySelectProps) => {
  const excludedIdSet = useMemo(
    () => new Set((excludeIds ?? []).filter((id) => !!id).map((id) => String(id))),
    [excludeIds]
  );

  const useEntityQuery = getEntityHook(entityType);
  const entityQuery = useEntityQuery
    ? useEntityQuery(
        entityType && selectedId && selectedId !== "" ? [selectedId] : []
      )
    : { entities: [], isSuccess: false };

  const { entities: [entityData] = [] } = entityQuery;

  const [searchTerm, setSearchTerm] = useState<string>("");

  const { options: searchedOptions, isLoading: isLoadingOptions } =
    useEntitySearch({
      entityType,
      searchTerm,
      filters,
      alwaysEnabled
    });

  const options = useMemo(() => {
    const shouldExcludeId = (id: string | number | null | undefined) =>
      id != null && excludedIdSet.has(String(id));
    const result = searchedOptions.filter(
      (option) => !shouldExcludeId(option?.id)
    );

    // If we have a selected option, add it to the top of options if not already there
    if (entityData && selectedId && selectedId !== "") {
      const isReserved =
        entityType === EntityType.PERSON && isReservedPersonId(selectedId);
      const isSelectedExcluded = shouldExcludeId(selectedId);

      if (!isReserved) {
        const existingInOptions = result.some(
          (option) => option.id === selectedId
        );

        if (!existingInOptions) {
          result.unshift(
            getSingleSelectOption(
              entityData,
              entityType,
              isSelectedExcluded || !isEmpty(searchTerm)
            )
          );
        }
      }
    }

    return result;
  }, [entityData, searchedOptions, selectedId, searchTerm, entityType, excludeIds]);

  useEffect(() => {
    const selectableSearchedOptions = searchedOptions.filter(
      (option) => !excludedIdSet.has(String(option?.id))
    );

    // Auto-select single option
    if (
      autoSelectSingleOption &&
      selectableSearchedOptions.length === 1 &&
      isEmpty(searchTerm) &&
      (!selectedId ||
        selectedId === "" ||
        selectedId !== selectableSearchedOptions[0].id) &&
      onAutoSelect
    ) {
      onAutoSelect(selectableSearchedOptions[0].id);
    }
  }, [
    searchedOptions,
    excludeIds,
    excludedIdSet,
    autoSelectSingleOption,
    selectedId,
    searchTerm,
    onAutoSelect
  ]);

  const onSearch = useCallback(
    (newSearchVal: string) => setSearchTerm(newSearchVal.toLowerCase()),
    []
  );

  return {
    options,
    isLoading: isLoadingOptions,
    onSearch,
    searchTerm,
    setSearchTerm
  };
};
